/**
 * Client-side performance metrics: the browser half of the perf.* pipeline
 * described in shared/perf.ts. Aggregation is pure and module-scoped (three
 * Reservoirs); everything that touches the DOM, timers or PerformanceObserver
 * lives behind startPerf() so importing this file is safe from Node (see
 * tests/perf-client.test.ts, same pattern as src/telemetry.ts).
 */

import {
  Reservoir,
  round1,
  type PerfApiDetail,
  type PerfBoardDetail,
  type PerfLiveDetail,
  type PerfNavDetail,
  type PerfScreenDetail,
  type PerfSessionDetail,
  type ServerTiming,
} from "../shared/perf.ts";
import { flushForUnload, report } from "./telemetry.ts";
import { takeLiveCounters } from "./live.ts";

const RESERVOIR_CAPACITY = 32;
const FLUSH_INTERVAL_MS = 60_000;
const NAV_REPORT_DELAY_MS = 10_000;

const apiReservoir = new Reservoir(RESERVOIR_CAPACITY);
const boardReservoir = new Reservoir(RESERVOIR_CAPACITY);
const liveReservoir = new Reservoir(RESERVOIR_CAPACITY);
const screenReservoir = new Reservoir(RESERVOIR_CAPACITY);

/** Endpoints that would recurse into their own perf tracking if sampled. */
const EXCLUDED_ROUTES = new Set(["/api/client/events", "/api/perf/summary", "/api/events"]);

/** True when a (templated) route should be sampled by recordApi. Exported so it's testable without a browser. */
export function isTrackedRoute(route: string): boolean {
  return !EXCLUDED_ROUTES.has(route);
}

/** Adds one API request's timing to the reservoir. Called from src/api.ts after every request settles. */
export function recordApi(route: string, method: string, ms: number, timing: ServerTiming, status: number): void {
  try {
    if (!isTrackedRoute(route)) return;
    apiReservoir.add(`${method} ${route}`, ms, {
      error: status === 0 || status >= 500,
      extra: { server: timing.app, overhead: timing.app === null ? null : ms - timing.app },
    });
  } catch {
    /* never throws */
  }
}

/** Time from the board mounting (or its status changing) to its first list render. */
export function recordBoard(status: "open" | "done", ms: number): void {
  try {
    boardReservoir.add(status, ms);
  } catch {
    /* never throws */
  }
}

/** Time from a live change notification to the refetch it triggered settling. */
export function recordLive(ms: number): void {
  try {
    liveReservoir.add("live", ms);
  } catch {
    /* never throws */
  }
}

/**
 * Time from a screen mounting to its content showing (shared/perf.ts PerfScreenDetail): `press`
 * is the press that opened it to its mount, `fetch` the wait for its request, each null when unknown.
 */
export function recordScreen(screen: string, ms: number, extra: { press: number | null; fetch: number | null }): void {
  try {
    screenReservoir.add(screen, ms, { extra: { press: extra.press, fetch: extra.fetch } });
  } catch {
    /* never throws */
  }
}

/** A screen reservoir entry as its PerfScreenDetail. Pure; unit tested directly. */
export function screenDetailFrom(entry: ReturnType<Reservoir["flush"]>[number]): PerfScreenDetail {
  return { screen: entry.key, n: entry.n, p50: entry.p50, p95: entry.p95, max: entry.max, press_p50: entry.extra.press ?? null, fetch_p50: entry.extra.fetch ?? null };
}

/** Splits a Reservoir entry's "METHOD /route" key back into a PerfApiDetail. Pure; unit tested directly. */
export function apiDetailFrom(entry: ReturnType<Reservoir["flush"]>[number]): PerfApiDetail {
  const spaceAt = entry.key.indexOf(" ");
  const method = spaceAt === -1 ? entry.key : entry.key.slice(0, spaceAt);
  const route = spaceAt === -1 ? "" : entry.key.slice(spaceAt + 1);
  return {
    route,
    method,
    n: entry.n,
    p50: entry.p50,
    p95: entry.p95,
    max: entry.max,
    server_p50: entry.extra.server ?? null,
    overhead_p50: entry.extra.overhead ?? null,
    errors: entry.errors,
  };
}

function hasQueued(): boolean {
  return apiReservoir.size > 0 || boardReservoir.size > 0 || liveReservoir.size > 0 || screenReservoir.size > 0;
}

/** Drains the three reservoirs and reports one perf.* event per key. A no-op when nothing is queued. */
export function flushPerf(): void {
  try {
    for (const entry of apiReservoir.flush()) report("perf.api", "", apiDetailFrom(entry));
    for (const entry of boardReservoir.flush()) {
      const detail: PerfBoardDetail = { status: entry.key as "open" | "done", n: entry.n, p50: entry.p50, p95: entry.p95, max: entry.max };
      report("perf.board", "", detail);
    }
    for (const entry of liveReservoir.flush()) {
      const detail: PerfLiveDetail = { n: entry.n, p50: entry.p50, p95: entry.p95, max: entry.max };
      report("perf.live", "", detail);
    }
    for (const entry of screenReservoir.flush()) report("perf.screen", "", screenDetailFrom(entry));
  } catch {
    /* never throws */
  }
}

// ---------- browser wiring ----------

let started = false;
let fcp: number | null = null;
let lcp: number | null = null;
let longtaskCount = 0;
let longtaskMs = 0;
let longtaskMax = 0;
/** Slowest "event" timing entry seen (proxy for INP); null when unsupported or nothing has fired yet. */
let inpMax: number | null = null;
let navReported = false;
let lastSessionAt = 0;

/** Runs `fn`, swallowing anything it throws: a missing API (Safari lacks longtask/event timing; private mode can block storage) must never break the app. */
function safeCall(fn: () => void): void {
  try {
    fn();
  } catch {
    /* see above */
  }
}

function safeObserve(type: string, callback: (list: PerformanceObserverEntryList) => void, extra?: Record<string, unknown>): void {
  safeCall(() => {
    if (typeof PerformanceObserver === "undefined") return;
    if (PerformanceObserver.supportedEntryTypes && !PerformanceObserver.supportedEntryTypes.includes(type)) return;
    const obs = new PerformanceObserver((list) => safeCall(() => callback(list)));
    obs.observe({ type, buffered: true, ...extra } as PerformanceObserverInit);
  });
}

function installObservers(): void {
  safeObserve("paint", (list) => {
    for (const entry of list.getEntries()) {
      if (entry.name === "first-contentful-paint" && fcp === null) fcp = entry.startTime;
    }
  });
  safeObserve("largest-contentful-paint", (list) => {
    for (const entry of list.getEntries()) lcp = entry.startTime; // keep the latest candidate
  });
  safeObserve("longtask", (list) => {
    for (const entry of list.getEntries()) {
      longtaskCount++;
      longtaskMs += entry.duration;
      longtaskMax = Math.max(longtaskMax, entry.duration);
    }
  });
  // durationThreshold keeps this to interactions that would actually show up as jank; the INP
  // proxy is just the slowest one seen this session (see PerfSessionDetail.inp).
  safeObserve(
    "event",
    (list) => {
      for (const entry of list.getEntries()) {
        if (inpMax === null || entry.duration > inpMax) inpMax = entry.duration;
      }
    },
    { durationThreshold: 40 },
  );
}

function buildNavDetail(): PerfNavDetail {
  const navEntry = performance.getEntriesByType?.("navigation")[0] as PerformanceNavigationTiming | undefined;
  const resources = (performance.getEntriesByType?.("resource") ?? []) as PerformanceResourceTiming[];
  const js = resources.find((r) => r.name.includes("/assets/") && r.name.endsWith(".js"));
  const css = resources.find((r) => r.name.includes("/assets/") && r.name.endsWith(".css"));
  const nav = navigator as Navigator & { connection?: { effectiveType?: string } };
  return {
    ttfb: navEntry ? round1(navEntry.responseStart) : null,
    fcp: fcp === null ? null : round1(fcp),
    lcp: lcp === null ? null : round1(lcp),
    dom_interactive: navEntry ? round1(navEntry.domInteractive) : null,
    load: navEntry ? round1(navEntry.loadEventEnd) : null,
    html_bytes: navEntry ? Math.round(navEntry.transferSize) : null,
    js_bytes: js ? Math.round(js.transferSize) : null,
    js_ms: js ? round1(js.duration) : null,
    js_cached: js ? js.transferSize === 0 && js.decodedBodySize > 0 : null,
    css_bytes: css ? Math.round(css.transferSize) : null,
    standalone: matchMedia("(display-mode: standalone)").matches,
    nav_type: navEntry ? navEntry.type : null,
    connection: nav.connection?.effectiveType ?? null,
  };
}

function reportNavOnce(): void {
  if (navReported) return;
  navReported = true;
  safeCall(() => report("perf.nav", "", buildNavDetail()));
}

/** Reports perf.nav once: at the first of the page hiding, or 10s after load. */
function scheduleNavReport(): void {
  safeCall(() => {
    const arm = () => window.setTimeout(() => reportNavOnce(), NAV_REPORT_DELAY_MS);
    // startPerf can run after "load" already fired (it's wired up from an effect, not a script
    // tag), so a readyState check stands in for a load event we may have missed.
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", () => safeCall(arm), { once: true });
  });
}

/** Counters since the previous session report (or since startPerf), then reset. */
function reportSession(): void {
  const now = performance.now();
  const duration = now - lastSessionAt;
  lastSessionAt = now;
  const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
  const counters = takeLiveCounters();
  const detail: PerfSessionDetail = {
    duration_ms: Math.round(duration),
    longtasks: longtaskCount,
    longtask_ms: round1(longtaskMs),
    longtask_max: round1(longtaskMax),
    inp: inpMax === null ? null : round1(inpMax),
    heap_mb: mem ? round1(mem.usedJSHeapSize / 1_048_576) : null,
    ws_reconnects: counters.reconnects,
    ws_fallbacks: counters.fallbacks,
  };
  report("perf.session", "", detail);
  longtaskCount = 0;
  longtaskMs = 0;
  longtaskMax = 0;
  inpMax = null;
}

/**
 * Hidden/pagehide handler: reports perf.nav (if it hasn't fired yet) and
 * perf.session, flushes the reservoirs, then asks telemetry to send
 * everything right away with a beacon. telemetry's own hidden/pagehide flush
 * runs first (startTelemetry registers before startPerf), so this is a
 * second beacon for whatever we just queued.
 */
function onHideOrUnload(): void {
  safeCall(reportNavOnce);
  safeCall(reportSession);
  safeCall(flushPerf);
  safeCall(flushForUnload);
}

function installUnloadReporting(): void {
  safeCall(() => {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") onHideOrUnload();
    });
    window.addEventListener("pagehide", onHideOrUnload);
  });
}

/** Long sessions report progressively instead of waiting for hide/unload. */
function installPeriodicFlush(): void {
  safeCall(() => {
    window.setInterval(() => {
      if (hasQueued()) flushPerf();
    }, FLUSH_INTERVAL_MS);
  });
}

/** Installs all browser-only perf wiring. Call once, right after startTelemetry(). */
export function startPerf(): void {
  if (started) return;
  started = true;
  safeCall(() => {
    lastSessionAt = performance.now();
    installObservers();
    scheduleNavReport();
    installUnloadReporting();
    installPeriodicFlush();
  });
}
